<script lang="ts" setup>
import type { RecipientGift } from '@consumer/types/serializers'
import type GiftTemplate from '@consumer/types/GiftTemplate'
import { greetingCards } from '@consumer/api'
import { setupRecipientLanding } from '@consumer/modules/recipientGiftLandingV2/composables'
import { useDefaultTheme } from '@consumer/composables/themes'
import V2ModalFullscreen from '@consumer/components/V2/V2ModalFullscreen.vue'
import LoadingIndicator from '@consumer/components/LoadingIndicator.vue'
import GIconButton from '@consumer/components/GIconButton.vue'
import { faClose } from '@fortawesome/pro-regular-svg-icons'
import { notifyError } from '@shared/errors'
import GiftShowcaseFlow from './GiftShowcaseFlow.vue'

// Fullscreen host for the guided greeting-card walkthrough (GiftShowcaseFlow).
//   demo    — homepage "See demo"; generic sample gift fetched from the
//             backend. A gift may still be passed (e.g. the multi-place page),
//             in which case the demo shows THAT gift's giftly card.
//   preview — checkout cart / gift wizard; the buyer's actual gift is passed in.
// Always runs the flow in preview mode (setupRecipientLanding preview: true) so
// nothing here marks the gift opened or hits the backend.
// Matches openGiftShowcaseModal's service API: preview may hand us an
// in-progress GiftTemplate (e.g. the multi-place page); demo passes nothing.
// Both are read as a RecipientGift below (they share the fields the flow uses).
const { mode, gift: giftProp } = defineProps<{
  mode: 'demo' | 'preview'
  gift?: RecipientGift | GiftTemplate
}>()

// The giftly (redemption) card is a v3 render driven entirely by these fields;
// grafting them onto the generic demo gift swaps only the card, leaving the
// sample greeting / note / photos intact.
const giftlyFieldsFrom = (g: RecipientGift | GiftTemplate) => ({
  giftlyVersion: g.giftlyVersion,
  giftlyMetadata: g.giftlyMetadata,
  amount: g.amount,
  places: g.places,
  // Drop any server-rendered PNG so the v3 card renders client-side.
  giftlyImageUrl: undefined,
})

const open = ref(true)
const emit = defineEmits<{ close: [] }>()
const onClose = () => {
  emit('close')
  open.value = false
}

useDefaultTheme()

// Start in the loading state unless we already have a fully-previewable gift
// (one that carries a chosen greeting card). Demo mode has no gift at all;
// preview mode may hand us a gift with no card (e.g. the multi-place gift-idea
// page), in which case we borrow the real default card from the database below
// so the flow shows an actual greeting card rather than the blank fallback.
const hasCard = (g?: RecipientGift | GiftTemplate) => Boolean(g?.greeting?.card)
// Demo always fetches the sample gift (even when a gift is passed, since we
// only borrow its giftly card); preview can start from a passed carded gift.
const gift = ref<RecipientGift | undefined>(
  // A GiftTemplate is read as a RecipientGift here — they share the fields the
  // flow uses (see giftlyFieldsFrom / the greeting spread below).
  mode === 'preview' && hasCard(giftProp) ? (giftProp as RecipientGift) : undefined,
)
setupRecipientLanding(() => gift.value as RecipientGift, { preview: true })

onMounted(async () => {
  if (gift.value) return
  try {
    if (mode === 'demo') {
      // Generic sample gift (wraps the real default card server-side). When a
      // gift was passed (e.g. the multi-place page), swap in its giftly card.
      const demoGift = await greetingCards.demo<RecipientGift>()
      gift.value = giftProp
        ? { ...demoGift, ...giftlyFieldsFrom(giftProp) } as RecipientGift
        : demoGift
    }
    else {
      // Preview of a gift without a chosen card: attach a real showcase card
      // from the database so the recipient experience isn't shown with a blank
      // card (the "Happy Birthday" card, or the default when it's absent).
      const card = await greetingCards.showcaseCard()
      gift.value = { ...giftProp, greeting: { ...giftProp?.greeting, card } } as RecipientGift
    }
  }
  catch (error) {
    notifyError(error)
    onClose()
  }
})
</script>

<template>
  <V2ModalFullscreen
    name="gift-showcase-modal"
    :open="open"
    @close="onClose"
  >
    <!-- Solid white header with the flow's title centered and the close button
         pinned right. Sits in the flex flow (not an overlay), so the walkthrough
         below it starts under the header rather than behind it. -->
    <template #header>
      <div class="showcase-header">
        <span class="showcase-header__title">
          {{ mode === 'demo' ? 'Gift Demo' : 'Gift Preview' }}
        </span>

        <GIconButton
          :icon="faClose"
          size="medium"
          :label="mode === 'demo' ? 'Close demo' : 'Close preview'"
          data-testid="close-button"
          @click.prevent="onClose"
        />
      </div>
    </template>

    <div class="h-full w-full">
      <div v-if="!gift" class="h-full flex items-center justify-center">
        <LoadingIndicator size="xl"/>
      </div>
      <GiftShowcaseFlow v-else :mode="mode" @close="onClose"/>
    </div>
  </V2ModalFullscreen>
</template>

<style scoped lang="scss">
// In the flex flow (not an overlay): the solid bar takes its own height at the
// top and the walkthrough renders below it. The title is absolutely centered so
// the close button can sit at flex-end without shifting it off-center.
.showcase-header {
  align-items: center;
  background: #fff;
  display: flex;
  gap: 8px;
  height: 60px;
  justify-content: flex-end;
  padding: 20px 40px;
  position: relative;
  z-index: 30;
}

.showcase-header__title {
  color: var(--text-text-primary, #111e36);
  font-family: Gilroy, sans-serif;
  font-size: 16px;
  font-weight: 600;
  left: 50%;
  line-height: 130%;
  position: absolute;
  text-align: center;
  transform: translateX(-50%);
}
</style>
